import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Shield, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const ASVerifyPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [asn, setAsn] = useState('');

  const translations = {
    tr: {
      verifying: 'AS numarası doğrulanıyor...',
      success: 'AS Doğrulaması Başarılı!',
      successDesc: 'başarıyla doğrulandı. Artık BGP oturumları oluşturabilirsiniz.',
      error: 'Doğrulama Hatası',
      tokenNotFound: 'Doğrulama token\'ı bulunamadı',
      goToDashboard: 'Dashboard\'a Git',
      tryAgain: 'Tekrar Dene',
    },
    en: {
      verifying: 'Verifying AS number...',
      success: 'AS Verification Successful!',
      successDesc: 'has been verified successfully. You can now create BGP sessions.',
      error: 'Verification Error',
      tokenNotFound: 'Verification token not found',
      goToDashboard: 'Go to Dashboard',
      tryAgain: 'Try Again',
    }
  };

  const txt = translations[language] || translations.en;

  useEffect(() => {
    let isCalled = false;
    
    const verifyAS = async () => {
      // Prevent duplicate calls
      if (isCalled) return;
      isCalled = true;
      
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage(language === 'tr' ? 'Doğrulama token\'ı bulunamadı' : 'Verification token not found');
        return;
      }

      try {
        const response = await fetch('https://api.threat.live/client/dashboard/as/verify', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: token })
        });

        const data = await response.json();

        if (data.status === 'success') {
          setStatus('success');
          setAsn(data.asn || '');
          setMessage(data.message || '');
        } else {
          setStatus('error');
          setMessage(data.message || (language === 'tr' ? 'Doğrulama Hatası' : 'Verification Error'));
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.message || (language === 'tr' ? 'Doğrulama Hatası' : 'Verification Error'));
      }
    };

    verifyAS();
    
    return () => {
      isCalled = true; // Cleanup - prevent call if unmounted
    };
  }, []); // Empty dependency - run only once on mount

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="as-verify-page">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-cyan-500/5"></div>
      <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-lg blur opacity-75"></div>
              <div className="relative bg-gradient-to-r from-emerald-600 to-cyan-600 p-3 rounded-lg">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <span className="text-2xl font-bold tracking-tight bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent">
              Threat.live
            </span>
          </div>
        </div>

        <Card className="border-0 bg-white dark:bg-zinc-900 shadow-2xl">
          <CardContent className="pt-8 pb-8">
            {status === 'loading' && (
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
                <p className="text-lg font-medium">{txt.verifying}</p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-emerald-600">{txt.success}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  <span className="font-mono font-bold">{asn}</span> {txt.successDesc}
                </p>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white"
                >
                  {txt.goToDashboard}
                </Button>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-red-600">{txt.error}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white"
                >
                  {txt.goToDashboard}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ASVerifyPage;
